import * as React from "react";

import { cn } from "../lib/utils";

import { Button, ButtonProps } from "@/components/ui/button";
import { Input as BaseInput, InputProps } from "@/components/ui/input";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  initialValue: string;
  onEdit?: (value: string) => void;
  onCancel?: () => void;
  readOnly?: boolean;
}

const InlineEditContext = React.createContext<{
  value: string;
  setValue: (value: string) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  onEdit?: (value: string) => void;
  onCancel?: () => void;
  readOnly?: boolean;
} | null>(null);

const useInlineEdit = () => {
  const context = React.useContext(InlineEditContext);
  if (!context) {
    throw new Error("useInlineEdit should be used within InlineEditProvider");
  }
  return context;
};

const Rename = React.forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const { setIsEditing, isEditing, onEdit, value } = useInlineEdit();
  if (!isEditing) {
    return null;
  }
  return (
    <Button
      ref={ref}
      size="sm"
      {...props}
      onClick={() => {
        setIsEditing(false);
        onEdit?.(value);
      }}
    />
  );
});

const Cancel = React.forwardRef<React.ElementRef<"button">, ButtonProps>((props, ref) => {
  const { setIsEditing, isEditing, onCancel } = useInlineEdit();
  if (!isEditing) {
    return null;
  }
  return (
    <Button
      ref={ref}
      size="sm"
      variant="outline"
      {...props}
      onClick={() => {
        onCancel?.();
        setIsEditing(false);
      }}
    />
  );
});

const Input = React.forwardRef<React.ElementRef<"input">, InputProps>((props, ref) => {
  const { value, setValue, isEditing, setIsEditing, onEdit } = useInlineEdit();
  if (!isEditing) {
    return null;
  }

  return (
    <BaseInput
      ref={ref}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus
      {...props}
      value={value}
      onChange={event => setValue(event.target.value)}
      onKeyDown={event => {
        if (event.key === "Enter") {
          event.preventDefault();
          setIsEditing(false);
          onEdit?.(value);
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setIsEditing(false);
        }
      }}
    />
  );
});

const Trigger = React.forwardRef<React.ElementRef<"div">, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { isEditing } = useInlineEdit();
    if (isEditing) {
      return null;
    }
    return <div ref={ref} className={cn("min-w-0 overflow-hidden", className)} {...props} />;
  },
);

const EditTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, ...props }, ref) => {
  const { isEditing, setIsEditing, readOnly } = useInlineEdit();
  if (isEditing || readOnly) {
    return null;
  }
  return (
    <button
      ref={ref}
      type="button"
      aria-label="Rename"
      className={cn(
        "inline-flex flex-shrink-0 items-center justify-center rounded p-1 text-gray-400 opacity-0 transition-opacity hover:text-gray-600 group-hover/inline-edit:opacity-100",
        className,
      )}
      {...props}
      onClick={() => setIsEditing(true)}
    />
  );
});

const Root = React.forwardRef<HTMLDivElement, Props>(
  ({ initialValue, onEdit, onCancel, className, children, readOnly, ...props }, ref) => {
    const [value, setValue] = React.useState(initialValue);
    const [isEditing, setIsEditing] = React.useState(false);

    React.useEffect(() => {
      setValue(initialValue);
    }, [initialValue]);

    return (
      <InlineEditContext.Provider
        value={{
          value,
          setValue,
          isEditing,
          setIsEditing,
          readOnly,
          onEdit,
          onCancel,
        }}
      >
        <div
          ref={ref}
          className={cn("group/inline-edit flex min-w-0 items-center space-x-2 px-1", className)}
          {...props}
        >
          {children}
        </div>
      </InlineEditContext.Provider>
    );
  },
);

export { Cancel, EditTrigger, Input, Rename, Root, Trigger, useInlineEdit };
